import React, { useState, useCallback, useEffect } from "react";
import AuthPageLayout from "../../auth/components/AuthPageLayout";
import Transition from "../../../components/Transition";
import { AnimatePresence } from "framer-motion";
import OtpInput from "../../auth/pages/register/components/OtpInput";
import ButtonLoader from "../../../components/ButtonLoader";
import { useAppContext } from "../../../context/AppContext";
import { GenerateOTP } from "../../auth/network/api/GenerateOTP";
import { UserData } from "../network/api/UserData";

export default function UserEmailVerification({ email, handleLogout }) {

    const { appData, updateUserData, setUpdateUserData } = useAppContext();
    const [generatedOtp, setGeneratedOtp] = useState("");

    const [otp, setOtp] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    const [newEmail, setNewEmail] = useState(email);
    const [step, setStep] = useState("start");

    const handleSendOtp = async (not_auth) => {
        setIsLoading(true);
        setError("");
        try {
            const response = await GenerateOTP({ email: (newEmail || email).trim(), app_id: appData.id, not_auth });
            if (response.status === "success") {
                setGeneratedOtp(response.data.otp);
                console.log("Generated OTP:", response.data.otp);
                setStep("verify");
            } else {
                setError(response.message || "Failed to generate OTP");
            }
        } catch (err) {
            setError("An error occurred while generating OTP");
        }
        setIsLoading(false);
    };

    const raw = localStorage.getItem("userData");
    const userData = raw ? JSON.parse(raw) : null;

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        setError("");
        setIsLoading(true);

        if (otp.length !== 5) {
            setIsLoading(false);
            setError("Please enter a valid 5-digit OTP");
            return;
        }

        if (otp !== generatedOtp) {
            setIsLoading(false);
            setError("Please enter the correct OTP sent to your email");
            return;
        }

        //of otp is verified handle update email in the user profile and update the email-verification status§

        try {
            let profileForm = { email_verified: true }
            if (newEmail && newEmail.trim().toLowerCase() !== email.toLowerCase()) {
                profileForm.email = newEmail.trim();
            }
            
            const response = await UserData({ method: "PUT", formData: profileForm });
           
            if (response.status === "success") {

                const updatedUserData = {
                    ...userData,
                    user: {
                        ...userData.user,
                        email_verified: true,
                        email: newEmail && newEmail.trim() !== email ? newEmail.trim() : userData.user.email
                    },
                };

                localStorage.setItem("userData", JSON.stringify(updatedUserData));
                
                setIsLoading(false);
                setStep("success");
                setTimeout(() => {
                    setUpdateUserData(!updateUserData);
                }, 2000);
            } else {
                setError(response.message || "Failed to update update user account");
            }
        } catch (err) {
            setIsLoading(false);
            setError("An error occurred while updating user account.");
            return;
        }
    }, [otp, generatedOtp, email, newEmail, userData, updateUserData, setUpdateUserData]);

    useEffect(() => {
        if (otp.length === 5) {
            handleSubmit({ preventDefault: () => { } });
        } else {
            setError("");
        }

    }, [otp, handleSubmit]);


    return (
        <AuthPageLayout>
            {step === "start" && (
                <>
                    <h4 className="text-center">Verify Your Email</h4>
                    <p className="text-center text-dark mb-4">
                        We will send a verification code to your email: <strong>{newEmail}</strong>
                    </p>
                    {error && (
                        <AnimatePresence mode="wait">
                            <Transition key={error}>
                                <div className="alert alert-danger alert-dismissible alert-alt solid fade show">
                                    <strong>Error!</strong> {error}
                                </div>
                            </Transition>
                        </AnimatePresence>
                    )}
                    <div className="text-center">
                        <button
                            type="button"
                            className="btn btn-primary btn-block"
                            onClick={() => handleSendOtp(true)}
                            disabled={isLoading}
                        >
                            {isLoading ? <ButtonLoader /> : "Send Verification Code"}
                        </button>
                    </div>
                    <div className="text-center mt-4">
                        <button type="button" className="btn btn-dark light btn-block" onClick={() => { setError(""); setStep("change-email"); }}>
                            Change Email
                        </button>
                    </div>
                    <div className="new-account mt-4 text-center">
                        <p>Do it Later ? <button className="btn btn-link text-primary p-0" onClick={handleLogout}>Logout</button></p>
                    </div>
                </>
            )}
            {step === "verify" && (
                <>
                    <h4 className="text-center">Enter OTP</h4>
                    <p className="text-center text-dark mb-4">
                        An OTP has been sent to <strong>{newEmail}</strong>
                    </p>
                    {error && (
                        <AnimatePresence mode="wait">
                            <Transition key={error}>
                                <div className="alert alert-danger alert-dismissible alert-alt solid fade show">
                                    <strong>Error!</strong> {error}
                                </div>
                            </Transition>
                        </AnimatePresence>
                    )}
                    <form onSubmit={handleSubmit}>
                        <OtpInput length={5} onChange={(value) => setOtp(value)} isLoading={isLoading} />
                        <div className="text-center mt-4">
                            <button type="submit" className="btn btn-primary btn-block" disabled={isLoading || otp.length < 5}>
                                {isLoading ? <ButtonLoader /> : "Verify"}
                            </button>
                        </div>
                    </form>
                    <div className="text-center mt-4">
                        <button type="button" className="btn btn-dark light btn-block" onClick={() => { setError(""); setStep("change-email"); }}>
                            Change Email
                        </button>
                    </div>
                    <div className="new-account mt-3">
                        <p>Already verified? <button className="btn btn-link text-primary p-0" onClick={handleLogout}>Logout</button></p>
                    </div>
                </>
            )}

            {step === "change-email" && (
                <>
                    <h4 className="text-center">Change Email Address</h4>
                    <p className="text-center text-dark mb-4">
                        Enter your new email address below.
                    </p>

                    {error && (
                        <AnimatePresence mode="wait">
                            <Transition key={error}>
                                <div className="alert alert-danger alert-dismissible alert-alt solid fade show">
                                    <strong>Error!</strong> {error}
                                </div>
                            </Transition>
                        </AnimatePresence>
                    )}

                    <div className="mb-3">
                        <label htmlFor="newEmail" className="form-label">New Email Address</label>
                        <input
                            type="email"
                            className="form-control"
                            id="newEmail"
                            placeholder="example@email.com"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                        />
                    </div>



                    <div className="text-center mt-4">
                        <button
                            type="button"
                            className="btn btn-primary btn-block"
                            onClick={() => {
                                setError("");
                                if (!newEmail || !newEmail.includes("@")) {
                                    setError("Please enter a valid email address.");
                                    return;
                                }
                                handleSendOtp(null);
                            }}
                        >
                            Confirm & Send Verification Code
                        </button>
                    </div>

                    <div className="text-center mt-4">
                        <button type="button" className="btn btn-dark light btn-block" onClick={() => setStep("start")} disabled={isLoading}>
                            Cancel
                        </button>
                    </div>
                </>
            )}

            {step === "success" && (<>
                <h4 className="text-center">Email Verified Successfully</h4>
                <p className="text-center text-dark mb-4">
                    Your email has been successfully verified. You can now proceed to your account.
                </p>
                <div className="d-flex flex-column align-items-center justify-content-center text-center">
                    <svg className="success-tick mb-4" xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 52 52">
                        <circle className="success-circle" cx="26" cy="26" r="25" fill="none" />
                        <path className="success-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
                    </svg>
                </div>
            </>)}

        </AuthPageLayout>
    );
}